import {
  ApiError,
  Auth0AccountRole,
  CRMBoardFieldValue,
  CRMVisualizationColumnResponse,
  PublicFieldView,
  VisualizationsService,
} from "client/openapi";
import { ButtonColor } from "components/Button";
import {
  DialogAction,
  DialogActions,
  DialogClose,
  DialogContent,
} from "components/Dialog";
import FieldValueInput, { FieldValue } from "components/FieldValueInput";
import { useState } from "react";

export type MoveAsk = {
  visualizationId: number;
  column: CRMVisualizationColumnResponse;
  personType: Auth0AccountRole;
  personId: number;
  name: string;
  fields: PublicFieldView[];
};

function initialValue(field: PublicFieldView): FieldValue {
  const value = field.value as FieldValue | undefined;
  if (field.input === "subjects") {
    return Array.isArray(value) ? value : [];
  }
  return value ?? null;
}

function sameValue(a: FieldValue, b: FieldValue) {
  return JSON.stringify(a) === JSON.stringify(b);
}

/**
 * Shown when a card is dropped on a column whose rule does not pin one value
 * per field (an OR, a range, "contains" and so on). It lists that column's
 * fields filled with what is on file; Save writes them in one go.
 */
export default function MoveFieldsDialog({
  ask,
  onSaved,
}: {
  ask: MoveAsk;
  onSaved: (
    columns: CRMVisualizationColumnResponse[],
    inColumn: boolean,
    message?: string | null
  ) => void;
}) {
  const [values, setValues] = useState<Record<string, FieldValue>>(() =>
    Object.fromEntries(ask.fields.map((f) => [f.field, initialValue(f)]))
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string>();

  const setValue = (field: string, value: FieldValue) =>
    setValues((prev) => ({ ...prev, [field]: value }));

  function save() {
    const changed: CRMBoardFieldValue[] = ask.fields
      .filter((f) => !sameValue(values[f.field], initialValue(f)))
      .map((f) => ({ field: f.field, value: values[f.field] }));

    setSaving(true);
    setError(undefined);
    VisualizationsService.saveBoardPersonValues({
      visualizationId: ask.visualizationId,
      columnId: ask.column.id,
      requestBody: {
        person_type: ask.personType,
        person_id: ask.personId,
        values: changed,
      },
    })
      .then((response) => {
        onSaved(response.columns, response.in_column, response.message);
      })
      .catch((e: ApiError) => {
        const reason = e.body?.error ?? e.body?.detail;
        setError(
          typeof reason === "string"
            ? `That did not save: ${reason}`
            : "That did not save. Nothing was changed."
        );
      })
      .finally(() => setSaving(false));
  }

  return (
    <DialogContent
      className="dialog-content"
      // While saving the pop-up stays open, so a refused save is shown.
      showClose={!saving}
      onEscapeKeyDown={(e: KeyboardEvent) => {
        if (saving) e.preventDefault();
      }}
      onInteractOutside={(e: Event) => {
        if (saving) e.preventDefault();
      }}
    >
      <h2 className="text-xl font-semibold mb-4">
        Move {ask.name} to {ask.column.name}
      </h2>

      {ask.fields.length === 0 ? (
        <p className="text-sm text-gray-600">
          This column's fields no longer exist. Edit the column to set its rule
          again.
        </p>
      ) : (
        ask.fields.map((field) => (
          <label
            key={field.field}
            htmlFor={field.field}
            className="input-label mb-3"
          >
            {field.label}
            <div className="mt-1">
              <FieldValueInput
                field={field}
                value={values[field.field]}
                onChange={(value) => setValue(field.field, value)}
                disabled={saving}
              />
            </div>
          </label>
        ))
      )}

      {error && <p className="text-sm text-red-600 mt-2">{error}</p>}

      <DialogActions>
        <DialogAction
          primary={true}
          color={saving ? ButtonColor.GRAY : ButtonColor.SKYBLUE}
          onClick={save}
          disabled={saving || ask.fields.length === 0}
        >
          {saving ? (
            <span
              className="spinner-border spinner-border-sm"
              role="status"
              aria-hidden="true"
            />
          ) : (
            "Save"
          )}
        </DialogAction>
        <DialogClose asChild>
          <DialogAction color={ButtonColor.SKYBLUE} disabled={saving}>
            Cancel
          </DialogAction>
        </DialogClose>
      </DialogActions>
    </DialogContent>
  );
}
